import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Download, Loader2, FileText } from 'lucide-react'
import { motion } from 'framer-motion'
import { useAuth } from '@/hooks/useAuth'
import { workerInfoService } from '@/api/wizard/workerInfo.service'
import { generatePreviewHtml } from '@/services/cvPreviewService'
import { baseURL } from '@/api/axios'
import { colors, spacing } from '@/theme'

export function CvPreviewScreen() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(false)
  const [downloadProgress, setDownloadProgress] = useState(0)
  const [html, setHtml] = useState('')

  useEffect(() => {
    (async () => {
      try {
        const response = await workerInfoService.getWorkerInfo(user?.id || '')
        const profile = response.data
        const previewHtml = await generatePreviewHtml(profile, user?.referralCode)
        setHtml(previewHtml)
      } catch (error) {
        console.error('Error loading CV preview:', error)
      } finally {
        setLoading(false)
      }
    })()
  }, [user?.id])

  const handleDownload = async () => {
    if (!user?.id || downloading) return
    setDownloading(true)
    setDownloadProgress(0)

    try {
      const progressInterval = setInterval(() => {
        setDownloadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval)
            return 90
          }
          return prev + Math.random() * 15
        })
      }, 400)

      const response = await fetch(`${baseURL}/workers/${user.id}/cv/download`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      })

      clearInterval(progressInterval)
      setDownloadProgress(100)

      if (!response.ok) throw new Error('Download failed')

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `Hoja de Vida - ${user.firstName} ${user.lastName}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading PDF:', error)
    } finally {
      setTimeout(() => {
        setDownloading(false)
        setDownloadProgress(0)
      }, 600)
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div
        style={{
          position: 'sticky', top: 0, zIndex: 50,
          background: `linear-gradient(135deg, ${colors.primary[800]} 0%, ${colors.primary[900]} 50%, #1e3a5f 100%)`,
          padding: `${spacing[3]} ${spacing[5]}`,
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
        }}
      >
        <div style={{
          maxWidth: 1100, margin: '0 auto',
          display: 'flex', alignItems: 'center', gap: spacing[3],
          flexWrap: 'wrap',
        }}>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => navigate('/dashboard')}
            style={{
              background: 'rgba(255,255,255,0.12)',
              border: 'none',
              color: colors.white,
              width: 38, height: 38,
              borderRadius: 12,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
              backdropFilter: 'blur(4px)',
              flexShrink: 0,
            }}
          >
            <ArrowLeft size={20} />
          </motion.button>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FileText size={22} color="rgba(255,255,255,0.8)" />
              <span style={{
                color: colors.white, fontWeight: 700, fontSize: 18,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                Vista Previa de Hoja de Vida
              </span>
            </div>
            <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
              {user?.firstName} {user?.lastName}
            </span>
          </div>

          <motion.button
            whileHover={{ scale: downloading ? 1 : 1.03 }}
            whileTap={{ scale: downloading ? 1 : 0.97 }}
            onClick={handleDownload}
            disabled={downloading || loading}
            style={{
              background: downloading ? '#1e3a5f' : '#22c55e',
              border: 'none',
              color: colors.white,
              padding: '10px 22px',
              borderRadius: 12,
              display: 'flex', alignItems: 'center', gap: 8,
              cursor: downloading ? 'not-allowed' : 'pointer',
              fontWeight: 700, fontSize: 14,
              boxShadow: downloading ? 'none' : '0 4px 12px rgba(34,197,94,0.3)',
              opacity: (downloading || loading) ? 0.7 : 1,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Progress bar */}
            {downloading && (
              <span
                style={{
                  position: 'absolute', inset: 0,
                  background: 'rgba(255,255,255,0.15)',
                  width: `${Math.min(downloadProgress, 100)}%`,
                  transition: 'width 0.3s ease',
                }}
              />
            )}
            {downloading ? (
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                style={{ display: 'flex', position: 'relative', zIndex: 1 }}
              >
                <Loader2 size={18} />
              </motion.div>
            ) : (
              <Download size={18} />
            )}
            <span style={{ position: 'relative', zIndex: 1 }}>
              {downloading ? 'Descargando...' : 'Descargar PDF'}
            </span>
          </motion.button>
        </div>
      </div>

      {/* Preview area */}
      <div style={{
        flex: 1,
        padding: `${spacing[4]} ${spacing[3]}`,
        display: 'flex',
        justifyContent: 'center',
      }}>
        {loading ? (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            height: '60vh', flexDirection: 'column', gap: 16,
          }}>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              style={{
                width: 40, height: 40,
                borderRadius: '50%',
                border: `4px solid ${colors.gray[200]}`,
                borderTopColor: colors.primary[500],
              }}
            />
            <span style={{ color: colors.gray[500], fontSize: 15, fontWeight: 500 }}>
              Cargando vista previa...
            </span>
          </div>
        ) : html ? (
          <div
            style={{
              width: '100%',
              maxWidth: 1100,
              borderRadius: 16,
              overflow: 'hidden',
              boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
            }}
          >
            <iframe
              ref={iframeRef}
              srcDoc={html}
              title="Vista previa de hoja de vida"
              style={{
                width: '100%',
                height: 'calc(100vh - 100px)',
                border: 'none',
                background: 'white',
              }}
            />
          </div>
        ) : (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            textAlign: 'center', padding: spacing[12],
            color: colors.gray[500], maxWidth: 400,
          }}>
            <FileText size={48} style={{ opacity: 0.3, marginBottom: 16 }} />
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: colors.gray[700] }}>
              No se pudo cargar la vista previa
            </h3>
            <p style={{ color: colors.gray[500] }}>
              Completa tu información en el wizard primero.
            </p>
          </div>
        )}
      </div>

      {/* Overlay loading modal */}
      {downloading && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 100,
            background: 'rgba(0,0,0,0.4)',
            backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            style={{
              background: colors.white,
              borderRadius: 24,
              padding: spacing[8],
              textAlign: 'center',
              boxShadow: '0 24px 60px rgba(0,0,0,0.2)',
              maxWidth: 320,
              width: '90%',
            }}
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
              style={{
                width: 56, height: 56, margin: '0 auto 16px',
                borderRadius: '50%',
                border: `5px solid ${colors.gray[200]}`,
                borderTopColor: colors.primary[500],
              }}
            />
            <h3 style={{ fontSize: 18, fontWeight: 700, color: colors.gray[900], marginBottom: 8 }}>
              Generando PDF
            </h3>
            <p style={{ fontSize: 14, color: colors.gray[500], marginBottom: 16 }}>
              Preparando tu hoja de vida para descargar...
            </p>
            <div
              style={{
                width: '100%', height: 6,
                background: colors.gray[100],
                borderRadius: 999,
                overflow: 'hidden',
              }}
            >
              <motion.div
                style={{
                  height: '100%',
                  background: `linear-gradient(90deg, ${colors.primary[500]}, ${colors.secondary[500]})`,
                  borderRadius: 999,
                  width: `${Math.min(downloadProgress, 100)}%`,
                  transition: 'width 0.4s ease',
                }}
              />
            </div>
            <span style={{ fontSize: 12, color: colors.gray[400], marginTop: 8, display: 'block' }}>
              {Math.round(Math.min(downloadProgress, 100))}%
            </span>
          </motion.div>
        </div>
      )}
    </div>
  )
}
